import { Fragment, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { ErrorMessage, Field } from '@atlaskit/form';

import { wacToken } from '@/libraries/design/foundations';

/**
 * Self-serve help panel shown after a usage pack is selected in the enterprise
 * contact form (REQ-ENT-UI-04.03). It nudges customers who just need more usage
 * of an existing subscription toward the self-serve Admin Hub flow instead of
 * completing the sales contact form.
 */
const helpPanelStyle = css({
	boxSizing: 'border-box',
	marginBlockStart: '16px',
	marginBlockEnd: '8px',
	paddingBlock: '16px',
	paddingInline: '20px',
	borderRadius: '5px',
	backgroundColor: wacToken('color.neutral.light100'),
	borderInlineStartWidth: '4px',
	borderInlineStartStyle: 'solid',
	borderInlineStartColor: wacToken('color.primary.blue'),
});

const helpPanelEyebrowStyle = css({
	margin: '0',
	fontSize: '12px',
	fontWeight: '700',
	letterSpacing: '0.5px',
	textTransform: 'uppercase',
	color: wacToken('color.neutral.light800'),
});

const helpPanelTitleStyle = css({
	marginBlock: '4px',
	fontSize: '16px',
	fontWeight: '600',
	color: wacToken('color.primary.black'),
});

const helpPanelBodyStyle = css({
	margin: '0',
	fontSize: '14px',
	lineHeight: '1.5',
	color: wacToken('color.neutral.light900'),
});

export const UsagePackHelpPanel = () => {
	const intl = useIntl();

	return (
		<div css={helpPanelStyle} role="note" data-testid="usage-pack-help-panel">
			<p css={helpPanelEyebrowStyle}>
				{intl.formatMessage({
					id: 'usagePacks.helpPanel.eyebrow',
					defaultMessage: 'Quoting help',
				})}
			</p>
			<p css={helpPanelTitleStyle}>
				{intl.formatMessage({
					id: 'usagePacks.helpPanel.title',
					defaultMessage: 'Need more usage?',
				})}
			</p>
			<p css={helpPanelBodyStyle}>
				{intl.formatMessage({
					id: 'usagePacks.helpPanel.body',
					defaultMessage:
						'Billing admins can buy prepaid monthly usage packs directly from Atlassian Administration. You don’t need a quote for this.',
				})}
			</p>
		</div>
	);
};

const estimatedUsageInputStyle = css({
	boxSizing: 'border-box',
	display: 'block',
	paddingInlineStart: '13px',
	paddingInlineEnd: '20px',
	minHeight: '48px',
	width: '100%',
	fontWeight: '400',
	letterSpacing: 'normal',
	textAlign: 'start',
	lineHeight: '2',
	borderRadius: '5px',
	color: wacToken('color.primary.black'),
	backgroundColor: wacToken('color.neutral.light100'),
	border: 'none',
	cursor: 'text',
	WebkitAppearance: 'none',
	':focus': {
		borderColor: wacToken('color.primary.blue'),
		borderStyle: 'solid',
		borderWidth: '1px',
		outline: '0',
	},
	':focus-visible': {
		borderColor: wacToken('color.primary.blue'),
		borderStyle: 'solid',
		borderWidth: '1px',
		outline: '0',
	},
});

interface EstimatedUsageInputProps {
	onChangeHandler?: (value: string) => void;
}

const EstimatedUsageInput = ({ onChangeHandler }: EstimatedUsageInputProps) => {
	const [value, setValue] = useState('');

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = event.target.value;
		setValue(inputValue);
		onChangeHandler?.(inputValue);
	};

	return (
		<input
			css={estimatedUsageInputStyle}
			type="number"
			min="1"
			value={value}
			onChange={handleChange}
		/>
	);
};

interface EstimatedUsageFieldProps {
	setFieldValue: (name: string, value: unknown) => void;
}

/**
 * Required numeric field capturing the customer's estimated additional monthly
 * usage for the selected usage pack (REQ-ENT-UI-04.04). The field is keyed on
 * the selected product by the caller so it resets when the product changes.
 */
export const EstimatedUsageField = ({ setFieldValue }: EstimatedUsageFieldProps) => {
	const intl = useIntl();

	// Append a plain-text asterisk to match every other required field on this
	// form (e.g. "Your question"), which render a dark-grey text asterisk via the
	// label string. Using `<Field isRequired>` instead would render @atlaskit's
	// built-in RequiredAsterisk in red (color.text.danger), which is visually
	// inconsistent with the rest of the form.
	const label =
		intl.formatMessage({
			id: 'usagePacks.estimatedUsage.label',
			defaultMessage: 'Estimated amount of additional usage per month',
		}) + ' *';

	const errorMessage = intl.formatMessage({
		id: 'usagePacks.estimatedUsage.error',
		defaultMessage: 'Enter your estimated additional usage per month.',
	});

	// A `validate` prop is required for @atlaskit/form (final-form) to enumerate
	// this field during submission — without it the field is silently skipped and
	// the form would submit with an empty value (see HandleSubmit in the section
	// files). This enforces REQ-ENT-UI-04.04 AC-02 (required) and AC-03 (numeric).
	const validate = (value?: number | string) => {
		if (value === undefined || value === null || value === '') {
			return errorMessage;
		}
		return undefined;
	};

	return (
		<div css={css({ paddingBlockStart: '16px' })}>
			<Field name={'estimatedUsage'} label={label} validate={validate}>
				{({ error }) => (
					<Fragment>
						<EstimatedUsageInput
							onChangeHandler={(usage) => {
								setFieldValue('estimatedUsage', usage ? Number(usage) : '');
							}}
						/>
						{error && <ErrorMessage>{errorMessage}</ErrorMessage>}
					</Fragment>
				)}
			</Field>
		</div>
	);
};
